/**
 * Shared utility functions for storage backends
 */

import type { SnapshotMetadata, SnapshotReason } from './types.js';

/**
 * Compute SHA-256 hash of content (for sync verification)
 */
export async function computeHash(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Sanitize a filename to allow only safe characters
 */
export function sanitizeFilename(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\-_\s]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 50);
}

/**
 * Pick N items distributed evenly from array
 */
export function pickDistributed<T>(items: T[], n: number): T[] {
  if (items.length <= n) return items;
  const step = items.length / n;
  return Array.from({ length: n }, (_, i) => items[Math.floor(i * step)]);
}

/**
 * Pick latest snapshot per day
 */
export function pickOnePerDay<T extends { timestamp: number }>(items: T[]): T[] {
  const byDay: Record<string, T> = {};
  for (const snap of items) {
    const day = new Date(snap.timestamp).toDateString();
    if (!byDay[day] || snap.timestamp > byDay[day].timestamp) {
      byDay[day] = snap;
    }
  }
  return Object.values(byDay);
}

/**
 * Pick latest snapshot per week
 */
export function pickOnePerWeek<T extends { timestamp: number }>(items: T[]): T[] {
  const byWeek: Record<string, T> = {};
  for (const snap of items) {
    const date = new Date(snap.timestamp);
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay());
    const weekKey = weekStart.toDateString();
    if (!byWeek[weekKey] || snap.timestamp > byWeek[weekKey].timestamp) {
      byWeek[weekKey] = snap;
    }
  }
  return Object.values(byWeek);
}

/**
 * Pick latest snapshot per month, up to maxMonths
 */
export function pickOnePerMonth<T extends { timestamp: number }>(
  items: T[],
  maxMonths: number
): T[] {
  const byMonth: Record<string, T> = {};
  for (const snap of items) {
    const date = new Date(snap.timestamp);
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
    if (!byMonth[monthKey] || snap.timestamp > byMonth[monthKey].timestamp) {
      byMonth[monthKey] = snap;
    }
  }
  // Sort by date and take most recent months
  return Object.values(byMonth)
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, maxMonths);
}

/**
 * Apply retention policy to snapshots for a file
 * Retention policy:
 * - Today: keep all
 * - Yesterday: keep up to 5 (distributed)
 * - Days 2-7: keep 1 per day
 * - Weeks 2-4: keep 1 per week
 * - Older: keep 1 per month (up to 6 months)
 * - Hard cap: 30 snapshots per file
 */
export function applyRetentionPolicy<T extends { timestamp: number }>(snapshots: T[]): T[] {
  if (snapshots.length <= 5) return snapshots;

  const now = Date.now();
  const DAY = 24 * 60 * 60 * 1000;
  const WEEK = 7 * DAY;

  // Get start of today
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTodayMs = startOfToday.getTime();
  const startOfYesterday = startOfTodayMs - DAY;

  // Categorize snapshots by age
  const today: T[] = [];
  const yesterday: T[] = [];
  const pastWeek: T[] = [];    // days 2-7
  const pastMonth: T[] = [];   // weeks 2-4
  const older: T[] = [];       // 1+ months

  for (const snap of snapshots) {
    const age = now - snap.timestamp;

    if (snap.timestamp >= startOfTodayMs) {
      today.push(snap);
    } else if (snap.timestamp >= startOfYesterday) {
      yesterday.push(snap);
    } else if (age < WEEK) {
      pastWeek.push(snap);
    } else if (age < 4 * WEEK) {
      pastMonth.push(snap);
    } else {
      older.push(snap);
    }
  }

  // Apply retention rules
  const kept = [
    ...today,                                // Keep all today
    ...pickDistributed(yesterday, 5),        // Up to 5 from yesterday
    ...pickOnePerDay(pastWeek),              // 1 per day for past week
    ...pickOnePerWeek(pastMonth),            // 1 per week for past month
    ...pickOnePerMonth(older, 6)             // 1 per month, max 6 months
  ];

  // Hard cap at 30 per file
  return kept.slice(0, 30);
}

/**
 * Check if a snapshot should be created based on time-based rules
 */
export function shouldCreateSnapshot(
  lastSnapshot: SnapshotMetadata | null
): SnapshotReason | null {
  const now = Date.now();
  const HOUR = 60 * 60 * 1000;

  // Get start of today (midnight)
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTodayMs = startOfToday.getTime();

  if (!lastSnapshot) {
    // No history for this file - create first snapshot
    return 'initial';
  }

  // First save of the day for this file?
  if (lastSnapshot.timestamp < startOfTodayMs) {
    return 'daily_first';
  }

  // Hourly snapshot (if more than 1 hour since last snapshot for this file)
  if (now - lastSnapshot.timestamp > HOUR) {
    return 'hourly';
  }

  return null;
}

/**
 * Generate a filesystem-safe ISO8601 timestamp for snapshot IDs
 */
export function generateSnapshotId(timestamp: number = Date.now()): string {
  return new Date(timestamp).toISOString()
    .replace(/:/g, '-')
    .replace(/\.\d{3}/, '');
}
